/**
 * Simple recurrence rules for recurring tasks and compliance items.
 */
import { addDays, addMonths, type ISODate, parts } from "./dates.ts";

export type RecurrenceFrequency = "daily" | "weekly" | "monthly" | "yearly";

export interface RecurrenceRule {
  frequency: RecurrenceFrequency;
  interval: number;
  /** Day-of-month anchor for monthly/yearly rules so month-end dates don't drift. */
  anchorDay?: number | null;
  until?: ISODate | null;
}

/** The next occurrence after `date`, or null once past `until`. */
export function nextOccurrence(date: ISODate, rule: RecurrenceRule): ISODate | null {
  const interval = Math.max(1, rule.interval);
  const anchor = rule.anchorDay ?? parts(date).d;
  let next: ISODate;
  switch (rule.frequency) {
    case "daily":
      next = addDays(date, interval);
      break;
    case "weekly":
      next = addDays(date, 7 * interval);
      break;
    case "monthly":
      next = addMonths(date, interval, anchor);
      break;
    case "yearly":
      next = addMonths(date, 12 * interval, anchor);
      break;
  }
  return rule.until && next > rule.until ? null : next;
}

export function describeRule(rule: RecurrenceRule): string {
  const unit = { daily: "day", weekly: "week", monthly: "month", yearly: "year" }[rule.frequency];
  const every = rule.interval === 1 ? `Every ${unit}` : `Every ${rule.interval} ${unit}s`;
  return rule.until ? `${every} until ${rule.until}` : every;
}
